import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Weight {
  modifierWeightId: string;
  name: string;
  modifierWeightCode?: string;
  [key: string]: any;
}

interface Product {
  productId: string;
  [key: string]: any;
}

interface Menu {
  weights?: Weight[];
  [key: string]: any;
}

interface WeightSelectProps {
  value: Weight | null | Weight[];
  onChange: (weight: Weight | null | Weight[]) => void;
  product?: Product | null;
  menu?: Menu | null;
  multiple?: boolean;
  /** When provided, bypasses internal derivation and uses these weights directly */
  weights?: Weight[];
}

export function WeightSelect({
  value,
  onChange,
  product,
  menu,
  multiple = true,
  weights: externalWeights,
}: WeightSelectProps) {
  const [weights, setWeights] = useState<Weight[]>([]);
  const [weightsLoading, setWeightsLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // When external weights are provided, use them directly and auto-clear invalid selections
  useEffect(() => {
    if (externalWeights === undefined) return;
    setWeights(externalWeights);
    if (multiple && Array.isArray(value)) {
      const valid = value.filter(v => externalWeights.some(w => w.modifierWeightId === v.modifierWeightId));
      if (valid.length !== value.length) {
        onChangeRef.current(valid);
      }
    }
  }, [externalWeights, multiple]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalWeights !== undefined) return;

    setWeightsLoading(true);

    if (!product && menu && menu.weights && Array.isArray(menu.weights)) {
      setWeights(menu.weights);
      setWeightsLoading(false);
      return;
    }

    if (product && product.variants && Array.isArray(product.variants)) {
      const selectedVariant = product.selectedVariant || product.variants[0];

      if (selectedVariant && selectedVariant.slots && Array.isArray(selectedVariant.slots)) {
        const allWeights: Weight[] = [];
        selectedVariant.slots.forEach((slotItem: any) => {
          (slotItem.modifiers ?? []).forEach((modifierItem: any) => {
            (modifierItem.weights ?? []).forEach((weightItem: any) => {
              if (weightItem.weight) allWeights.push(weightItem.weight);
            });
          });
        });
        const uniqueWeights = allWeights.filter((w, i, a) => a.findIndex(x => x.modifierWeightId === w.modifierWeightId) === i);
        setWeights(uniqueWeights);
      } else {
        setWeights([]);
      }
    } else {
      setWeights([]);
    }

    setWeightsLoading(false);
  }, [product, menu, externalWeights]);

  // Make sure our controlled `value` never defaults to the *entire* list
  // If multiple: value must be an array; otherwise null
  const safeValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;


  const getLabel = (option: Weight) => {
    if (!option) return 'No weight';
    if (!option.name && !option.modifierWeightCode) return 'Unknown';
    return `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={Array.isArray(weights) ? weights : []}
      disableCloseOnSelect
      filterSelectedOptions
      value={safeValue}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.modifierWeightId === (val as Weight).modifierWeightId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = (!option.name && !option.modifierWeightCode)
          ? 'Unknown'
          : `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
        return (
          <li {...props} key={option.modifierWeightId || `weight-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) => {
        if (!multiple || !Array.isArray(tagValue)) return [];
        return tagValue.map((option, idx) => {
          if (!option) return null;
          const label = (!option.name && !option.modifierWeightCode)
            ? 'Unknown'
            : `${option.name ?? 'Unknown'} (${option.modifierWeightCode ?? 'Unknown'})`;
          return (
            <Chip
              {...getTagProps({ index: idx })}
              key={option.modifierWeightId}
              size="small"
              label={label}
            />
          );
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Weight"
          placeholder="Search for weights..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {weightsLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
